import axios from 'axios';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api, { API_URL } from '../config/api';

/**
 * Maszkolás közben a `user` már az IMITÁLT felhasználó — ez a doboz mondja meg,
 * hogy valójában ki ül a gép előtt. A backend a /auth/profile válaszában adja.
 */
export interface MasqueradeInfo {
  sessionId: string;
  originalUserId: string;
  originalEmail: string;
  originalFirstName: string;
  originalLastName: string;
  originalRole: string;
  expiresAt: string;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  organizationId?: string;
  organization?: {
    id: string;
    name: string;
    slug: string;
  };
  masquerade?: MasqueradeInfo;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  login: (email: string, password: string) => Promise<void>;
  startMasquerade: (userId: string) => Promise<void>;
  stopMasquerade: () => Promise<void>;
  register: (data: any) => Promise<void>;
  logout: () => void;
  fetchProfile: () => Promise<void>;
  initialize: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/auth/login', { email, password });
          const { user, access_token, refresh_token } = response.data;

          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', refresh_token);

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Login failed',
            isLoading: false,
          });
          throw error;
        }
      },

      register: async (data: any) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/auth/register', data);
          const { user, access_token, refresh_token } = response.data;

          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', refresh_token);

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Registration failed',
            isLoading: false,
          });
          throw error;
        }
      },

      startMasquerade: async (userId: string) => {
        await api.post('/masquerade/start', { userId });
        // A token változatlan marad — a szerver a következő kérésnél cseréli
        // ki az identitást, ezért a profilt újra kell húzni.
        await get().fetchProfile();
      },

      stopMasquerade: async () => {
        await api.post('/masquerade/stop');
        await get().fetchProfile();
      },

      logout: () => {
        // Maszkolás közbeni kijelentkezés zárja le a DB-sort is, különben egy
        // másik felület (vagy a visszalépés) tovább mutatná az imitált
        // identitást. Best-effort: a kijelentkezés nem függhet ettől a kéréstől,
        // és a tokent még a törlés ELŐTT kell a fejlécbe tenni.
        const token = localStorage.getItem('access_token');
        if (get().user?.masquerade && token) {
          axios
            .post(
              `${API_URL}/masquerade/stop`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            )
            .catch(() => {});
        }
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        set({
          user: null,
          isAuthenticated: false,
          error: null,
        });
      },

      fetchProfile: async () => {
        set({ isLoading: true });
        try {
          const response = await api.get('/auth/profile');
          set({
            user: response.data,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          // Clear tokens and state on profile fetch failure
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      // Initialize authentication state based on stored tokens
      initialize: async () => {
        const token = localStorage.getItem('access_token');
        const refreshToken = localStorage.getItem('refresh_token');

        if (token && refreshToken) {
          // Try to fetch profile to validate token
          try {
            const response = await api.get('/auth/profile');
            set({
              user: response.data,
              isAuthenticated: true,
              isLoading: false,
            });
          } catch (error) {
            // Token is invalid, clear everything
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        } else {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
